import { useEffect, useRef, useState, useCallback, createContext, useContext } from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import ELK from "elkjs/lib/elk.bundled.js";

import ConformanceActivityNode from "./ConformanceActivityNode";

// ── Constants ─────────────────────────────────────────────────────────────────

const NODE_WIDTH = 180;
const NODE_HEIGHT = 62;
const COLUMN_GAP = 240;

const ELK_OPTIONS = {
  "elk.algorithm": "layered",
  "elk.direction": "DOWN",
  "elk.edgeRouting": "ORTHOGONAL",
  "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
  "elk.layered.spacing.nodeNodeBetweenLayers": "80",
  "elk.spacing.nodeNode": "60",
  "elk.spacing.edgeNode": "12",
  "elk.layered.spacing.edgeNodeBetweenLayers": "20",
  "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
  "elk.separateConnectedComponents": "true",
  "elk.spacing.componentComponent": "60",
};

const elk = new ELK();

// Context that carries the edge-click callback into the custom edge component.
const EdgeClickContext = createContext(null);

// Returns the point at exactly 50% of the total arc length of a polyline.
// Produces better label placement than the arithmetic centroid for orthogonal
// paths, where many bend points cluster near the source/target ports.
function polylineMidpoint(pts) {
  let totalLen = 0;
  const segLens = [];
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    const len = Math.sqrt(dx * dx + dy * dy);
    segLens.push(len);
    totalLen += len;
  }
  let remaining = totalLen / 2;
  for (let i = 0; i < segLens.length; i++) {
    if (remaining <= segLens[i]) {
      const t = segLens[i] > 0 ? remaining / segLens[i] : 0;
      return {
        x: pts[i].x + t * (pts[i + 1].x - pts[i].x),
        y: pts[i].y + t * (pts[i + 1].y - pts[i].y),
      };
    }
    remaining -= segLens[i];
  }
  return pts[pts.length - 1];
}

// ── Custom edge: renders ELK-computed orthogonal polyline + optional label ────

function roundedPolylinePath(pts, r) {
  if (pts.length < 3)
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[i - 1], curr = pts[i], next = pts[i + 1];
    const dx1 = curr.x - prev.x, dy1 = curr.y - prev.y;
    const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const dx2 = next.x - curr.x, dy2 = next.y - curr.y;
    const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
    const ar = Math.min(r, len1 / 2, len2 / 2);
    const u1x = dx1 / len1, u1y = dy1 / len1;
    const u2x = dx2 / len2, u2y = dy2 / len2;
    const bx = curr.x - u1x * ar, by = curr.y - u1y * ar;
    const ax = curr.x + u2x * ar, ay = curr.y + u2y * ar;
    const sweep = (u1x * u2y - u1y * u2x) < 0 ? 0 : 1;
    d += ` L${bx},${by} A${ar},${ar} 0 0,${sweep} ${ax},${ay}`;
  }
  d += ` L${pts[pts.length - 1].x},${pts[pts.length - 1].y}`;
  return d;
}

const ElkRoutedEdge = ({ sourceX, sourceY, targetX, targetY, data, markerEnd, style }) => {
  const onEdgeClickFn = useContext(EdgeClickContext);
  let d, dVisual, midX, midY;
  if (data?.startPoint) {
    const pts = [data.startPoint, ...(data.bendPoints ?? []), data.endPoint];
    d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
    dVisual = roundedPolylinePath(pts, 6);
    ({ x: midX, y: midY } = polylineMidpoint(pts));
  } else {
    d = `M${sourceX},${sourceY} L${targetX},${targetY}`;
    dVisual = d;
    midX = (sourceX + targetX) / 2;
    midY = (sourceY + targetY) / 2;
  }
  const label = data?.label;
  const strokeColor = style?.stroke ?? "#94a3b8";
  // Approximate label width: ~5.5px per character + 8px horizontal padding, min 32px.
  const labelW = label ? Math.max(32, label.length * 5.5 + 8) : 0;
  const clickable = !!(data?.edgeKey && onEdgeClickFn);
  return (
    <>
      {/* Wide transparent hit area so the thin orthogonal path is easier to click.
          pointer-events:all is required — SVG ignores transparent strokes by default. */}
      <path
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={12}
        style={{ cursor: clickable ? "pointer" : "default", pointerEvents: "all" }}
        onMouseDown={clickable ? (e) => e.stopPropagation() : undefined}
        onClick={clickable
          ? (e) => { e.stopPropagation(); onEdgeClickFn(data.edgeKey, e.clientX, e.clientY); }
          : undefined}
      />
      <path d={dVisual} fill="none" style={style} markerEnd={markerEnd} className="react-flow__edge-path" />
      {label && (
        <g
          transform={`translate(${midX},${midY})`}
          style={{ cursor: clickable ? "pointer" : "default", pointerEvents: clickable ? "all" : "none" }}
          onClick={clickable
            ? (e) => { e.stopPropagation(); onEdgeClickFn(data.edgeKey, e.clientX, e.clientY); }
            : undefined}
        >
          <rect
            x={-labelW / 2} y={-8}
            width={labelW} height={16}
            rx={2}
            fill="white"
            stroke={strokeColor}
            strokeWidth={0.75}
          />
          <text textAnchor="middle" dominantBaseline="central" fontSize={9} fill={strokeColor}>
            {label}
          </text>
        </g>
      )}
    </>
  );
};

const NODE_TYPES = {
  conformanceActivity: ConformanceActivityNode,
  sectionLabel: ({ data }) => (
    <div
      style={{ width: data.width ?? 200 }}
      className="text-xs font-semibold text-gray-500 uppercase tracking-widest text-center border-b border-gray-200 pb-1 select-none pointer-events-none"
    >
      {data.label}
    </div>
  ),
};

const EDGE_TYPES = { elkRouted: ElkRoutedEdge };

// ── ELK helpers ───────────────────────────────────────────────────────────────

async function runElkLayout(nodes, edges) {
  if (nodes.length === 0) return { positionMap: {}, edgeRoutes: {}, graphWidth: 0, graphHeight: 0 };

  const result = await elk.layout({
    id: "root",
    layoutOptions: ELK_OPTIONS,
    children: nodes.map((n) => ({ id: n.id, width: NODE_WIDTH, height: NODE_HEIGHT })),
    edges: edges.map((e) => ({
      id: `e__${e.source}__${e.target}`,
      sources: [e.source],
      targets: [e.target],
    })),
  });

  const positionMap = {};
  for (const n of result.children ?? []) {
    positionMap[n.id] = { x: n.x ?? 0, y: n.y ?? 0 };
  }

  const edgeRoutes = {};
  for (const e of result.edges ?? []) {
    const section = e.sections?.[0];
    if (section) {
      edgeRoutes[e.id] = {
        startPoint: section.startPoint,
        endPoint: section.endPoint,
        bendPoints: section.bendPoints ?? [],
      };
    }
  }

  const xs = Object.values(positionMap).map((p) => p.x + NODE_WIDTH);
  const ys = Object.values(positionMap).map((p) => p.y + NODE_HEIGHT);
  return {
    positionMap,
    edgeRoutes,
    graphWidth: xs.length ? Math.max(...xs) : 0,
    graphHeight: ys.length ? Math.max(...ys) : 0,
  };
}

function buildSelfLoopRoute(nodePos) {
  const x  = nodePos.x;
  const cy = nodePos.y + NODE_HEIGHT / 2;
  const loopWidth = 55;  // px the loop extends to the right of the node
  const halfGap   = 12;  // half vertical span between the two handle points
  return {
    startPoint: { x: x + NODE_WIDTH, y: cy - halfGap },
    bendPoints: [
      { x: x + NODE_WIDTH + loopWidth, y: cy - halfGap },
      { x: x + NODE_WIDTH + loopWidth, y: cy + halfGap },
    ],
    endPoint: { x: x + NODE_WIDTH, y: cy + halfGap },
  };
}

function translateRoute(route, dx, dy) {
  if (!route) return null;
  const s = (p) => ({ x: p.x + dx, y: p.y + dy });
  return {
    startPoint: s(route.startPoint),
    endPoint: s(route.endPoint),
    bendPoints: route.bendPoints.map(s),
  };
}

function computeStartEnd(graph) {
  const hasIncoming = new Set(graph.edges.map((e) => e.target));
  const hasOutgoing = new Set(graph.edges.map((e) => e.source));
  return {
    start: new Set(graph.nodes.map((n) => n.id).filter((id) => !hasIncoming.has(id))),
    end:   new Set(graph.nodes.map((n) => n.id).filter((id) => !hasOutgoing.has(id))),
  };
}

// ── Edge hit-testing helpers ──────────────────────────────────────────────────

function distanceToSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function distanceToPolyline(p, pts) {
  let min = Infinity;
  for (let i = 0; i < pts.length - 1; i++) min = Math.min(min, distanceToSegment(p, pts[i], pts[i + 1]));
  return min;
}

// ── Temporal helpers ──────────────────────────────────────────────────────────

function formatDuration(seconds) {
  if (seconds < 60)    return `~${Math.round(seconds)}s`;
  if (seconds < 3600)  return `~${Math.round(seconds / 60)}min`;
  if (seconds < 86400) return `~${(seconds / 3600).toFixed(1)}h`;
  return `~${(seconds / 86400).toFixed(1)}d`;
}

function temporalEdgeColor(pairData, yellowThreshold, redThreshold) {
  if (!pairData) return "#94a3b8";
  const pct = Math.abs(pairData.pct_deviation);
  if (pct >= redThreshold)    return "#f87171"; // red-400
  if (pct >= yellowThreshold) return "#facc15"; // yellow-400
  return "#4ade80";                              // green-400
}

// ── Graph builder — called after ELK layout is known ─────────────────────────

function buildGraph(layout, visualResult, mode, temporalEdgeMap, yellowThreshold, redThreshold, observedEdgeFreqMap, sequentialAnnotation) {
  const { reference_graph, observed_graph, conformance_mapping } = visualResult;
  const { refLayout, obsLayout, refStartEnd, obsStartEnd, xOffset, headerY, refCenterX, obsCenterX } = layout;

  const isTemporalMode = mode === "temporal";

  // In sequential mode, build a set of reference edge keys for highlighting
  const refEdgeSet = new Set(reference_graph.edges.map((e) => `${e.source}→${e.target}`));

  const edgeDefaults = {
    type: "elkRouted",
    animated: false,
  };

  // ── Nodes ──

  const nodes = [
    {
      id: "__header_ref",
      type: "sectionLabel",
      position: { x: refCenterX - NODE_WIDTH / 2, y: headerY },
      data: { label: "Reference Process", width: NODE_WIDTH },
      selectable: false,
      style: { pointerEvents: "none" },
    },
    {
      id: "__header_obs",
      type: "sectionLabel",
      position: { x: obsCenterX - NODE_WIDTH / 2, y: headerY },
      data: { label: "Observed Process", width: NODE_WIDTH },
      selectable: false,
      style: { pointerEvents: "none" },
    },
    ...reference_graph.nodes.map((n) => ({
      id: `ref__${n.id}`,
      type: "conformanceActivity",
      position: refLayout.positionMap[n.id] ?? { x: 0, y: 0 },
      data: {
        label: n.label,
        conformanceStatus: isTemporalMode ? "match" : (conformance_mapping[n.id] ?? "match"),
        isStart: refStartEnd.start.has(n.id),
        isEnd:   refStartEnd.end.has(n.id),
      },
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      selectable: false,
      style: { pointerEvents: "none" },
    })),
    ...observed_graph.nodes.map((n) => ({
      id: `obs__${n.id}`,
      type: "conformanceActivity",
      position: {
        x: (obsLayout.positionMap[n.id]?.x ?? 0) + xOffset,
        y:  obsLayout.positionMap[n.id]?.y ?? 0,
      },
      data: {
        label: n.label,
        conformanceStatus: isTemporalMode ? "match" : (conformance_mapping[n.id] ?? "match"),
        isStart: obsStartEnd.start.has(n.id),
        isEnd:   obsStartEnd.end.has(n.id),
      },
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      selectable: false,
      style: { pointerEvents: "none" },
    })),
  ];

  // ── Edges ──

  const refEdges = reference_graph.edges.map((e) => {
    const pairKey = `${e.source}→${e.target}`;
    const pairData = temporalEdgeMap?.[pairKey];
    const color = "#94a3b8"; // reference edges are always neutral gray
    const label = isTemporalMode && pairData ? formatDuration(pairData.expected_center) : undefined;
    return {
      ...edgeDefaults,
      id: `ref__${e.source}__${e.target}`,
      source: `ref__${e.source}`,
      target: `ref__${e.target}`,
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
      style: { stroke: color, strokeWidth: 1.5 },
      data: {
        ...(e.source === e.target
          ? buildSelfLoopRoute(refLayout.positionMap[e.source] ?? { x: 0, y: 0 })
          : (refLayout.edgeRoutes[`e__${e.source}__${e.target}`] ?? null)),
        label,
      },
    };
  });

  const obsEdges = observed_graph.edges.map((e) => {
    const pairKey = `${e.source}→${e.target}`;
    const pairData = temporalEdgeMap?.[pairKey];
    const matched = refEdgeSet.has(pairKey);

    let color, strokeWidth, strokeDasharray, label;
    if (!matched) {
      // Deviation edge: not present in reference model — orange dashed in both modes
      color = "#f97316";
      strokeWidth = 2;
      strokeDasharray = "6 3";
      if (isTemporalMode) {
        label = pairData ? formatDuration(pairData.mean_observed) : undefined;
      } else {
        const freq = observedEdgeFreqMap?.[pairKey];
        label = freq
          ? (sequentialAnnotation === "relative"
              ? `${Math.round(freq.relativeProb * 100)}%`
              : `×${freq.count}`)
          : undefined;
      }
    } else if (isTemporalMode) {
      color = temporalEdgeColor(pairData, yellowThreshold, redThreshold);
      strokeWidth = 2;
      strokeDasharray = undefined;
      label = pairData ? formatDuration(pairData.mean_observed) : undefined;
    } else {
      color = "#94a3b8";
      strokeWidth = 1.5;
      strokeDasharray = undefined;
      const freq = observedEdgeFreqMap?.[pairKey];
      label = freq
        ? (sequentialAnnotation === "relative"
            ? `${Math.round(freq.relativeProb * 100)}%`
            : `×${freq.count}`)
        : undefined;
    }

    return {
      ...edgeDefaults,
      id: `obs__${e.source}__${e.target}`,
      source: `obs__${e.source}`,
      target: `obs__${e.target}`,
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
      style: { stroke: color, strokeWidth, ...(strokeDasharray && { strokeDasharray }) },
      data: {
        ...(e.source === e.target
          ? buildSelfLoopRoute({
              x: (obsLayout.positionMap[e.source]?.x ?? 0) + xOffset,
              y:  obsLayout.positionMap[e.source]?.y ?? 0,
            })
          : (translateRoute(obsLayout.edgeRoutes[`e__${e.source}__${e.target}`], xOffset, 0) ?? null)),
        label,
        edgeKey: (isTemporalMode ? !!temporalEdgeMap?.[pairKey] : true) ? pairKey : undefined,
      },
    };
  });

  return { nodes, edges: [...refEdges, ...obsEdges] };
}

// ── Component ─────────────────────────────────────────────────────────────────

const SplitConformanceGraph = ({ visualResult, mode, temporalEdgeMap, yellowThreshold, redThreshold, onHeightChange, onEdgeClick, onPaneClick, observedEdgeFreqMap, sequentialAnnotation }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [cursorPointer, setCursorPointer] = useState(false);
  const rfRef = useRef(null);
  const containerRef = useRef(null);
  const edgesRef = useRef([]); // kept in sync with edges state for hit-testing

  // Split ELK layout caches: ref side and obs side tracked independently so that
  // applying an object filter (which only changes observed_graph) doesn't re-run
  // ELK for the reference graph.
  const refLayoutCacheRef = useRef(null);
  const obsLayoutCacheRef = useRef(null);
  const prevRefGraphRef   = useRef(null);
  const prevObsGraphRef   = useRef(null);

  useEffect(() => {
    if (!visualResult) {
      refLayoutCacheRef.current = null;
      obsLayoutCacheRef.current = null;
      prevRefGraphRef.current   = null;
      prevObsGraphRef.current   = null;
      return;
    }
    const { reference_graph, observed_graph } = visualResult;

    (async () => {
      const refGraphChanged = reference_graph !== prevRefGraphRef.current;
      const obsGraphChanged = observed_graph  !== prevObsGraphRef.current;
      const anyGraphChanged = refGraphChanged || obsGraphChanged;

      // Run ELK only for the side(s) that actually changed.
      const [refLayout, obsLayout] = await Promise.all([
        (refGraphChanged || !refLayoutCacheRef.current)
          ? runElkLayout(reference_graph.nodes, reference_graph.edges)
          : Promise.resolve(refLayoutCacheRef.current),
        (obsGraphChanged || !obsLayoutCacheRef.current)
          ? runElkLayout(observed_graph.nodes, observed_graph.edges)
          : Promise.resolve(obsLayoutCacheRef.current),
      ]);

      if (refGraphChanged || !refLayoutCacheRef.current) {
        refLayoutCacheRef.current = refLayout;
        prevRefGraphRef.current   = reference_graph;
      }
      if (obsGraphChanged || !obsLayoutCacheRef.current) {
        obsLayoutCacheRef.current = obsLayout;
        prevObsGraphRef.current   = observed_graph;
      }

      const refStartEnd = computeStartEnd(reference_graph);
      const obsStartEnd = computeStartEnd(observed_graph);
      const xOffset     = refLayout.graphWidth + COLUMN_GAP;
      const refMinY     = Object.values(refLayout.positionMap).reduce((m, p) => Math.min(m, p.y), 0);
      const obsMinY     = Object.values(obsLayout.positionMap).reduce((m, p) => Math.min(m, p.y), 0);
      const headerY     = Math.min(refMinY, obsMinY) - 56;
      const refCenterX  = refLayout.graphWidth / 2;
      const obsCenterX  = xOffset + obsLayout.graphWidth / 2;

      const layout = { refLayout, obsLayout, refStartEnd, obsStartEnd, xOffset, headerY, refCenterX, obsCenterX };

      if (anyGraphChanged) {
        onHeightChange?.(Math.max(refLayout.graphHeight, obsLayout.graphHeight));
      }

      const { nodes: n, edges: e } = buildGraph(layout, visualResult, mode, temporalEdgeMap, yellowThreshold, redThreshold, observedEdgeFreqMap, sequentialAnnotation);
      setNodes(n);
      setEdges(e);
      edgesRef.current = e;

      if (anyGraphChanged) {
        requestAnimationFrame(() => rfRef.current?.fitView({ duration: 400, padding: 0.15 }));
      }
    })();
  }, [visualResult, mode, temporalEdgeMap, yellowThreshold, redThreshold, observedEdgeFreqMap, sequentialAnnotation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fit whenever the container is resized (tile drag handle).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let rafId;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => rfRef.current?.fitView({ padding: 0.15 }));
    });
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(rafId); };
  }, []);

  // Hit-test a screen position against stored ELK polylines. Returns edgeKey or null.
  const hitTestEdges = useCallback((clientX, clientY) => {
    if (!onEdgeClick || !rfRef.current) return null;
    const pos = rfRef.current.screenToFlowPosition({ x: clientX, y: clientY });
    const zoom = rfRef.current.getViewport()?.zoom ?? 1;
    const threshold = 8 / zoom; // 8 screen-px converted to flow units
    for (const edge of edgesRef.current) {
      if (!edge.data?.edgeKey || !edge.data?.startPoint) continue;
      const pts = [edge.data.startPoint, ...(edge.data.bendPoints ?? []), edge.data.endPoint];
      if (distanceToPolyline(pos, pts) < threshold) return edge.data.edgeKey;
      if (edge.data.label) {
        const { x: cx, y: cy } = polylineMidpoint(pts);
        const hw = Math.max(32, edge.data.label.length * 5.5 + 8) / 2;
        if (pos.x >= cx - hw && pos.x <= cx + hw && pos.y >= cy - 8 && pos.y <= cy + 8)
          return edge.data.edgeKey;
      }
    }
    return null;
  }, [onEdgeClick]);

  // Pane click: canvas background was clicked — close any open panel.
  const handlePaneClick = useCallback(() => {
    onPaneClick?.();
  }, [onPaneClick]);

  // Mouse-move on the container: update pointer cursor when hovering an ELK edge.
  const handleMouseMove = useCallback((event) => {
    if (!onEdgeClick) { if (cursorPointer) setCursorPointer(false); return; }
    setCursorPointer(hitTestEdges(event.clientX, event.clientY) !== null);
  }, [hitTestEdges, onEdgeClick, cursorPointer]);

  // Handle clicks on the container: if the click lands on an ELK edge polyline,
  // fire onEdgeClick directly. This bypasses the SVG path onClick, which was blocked
  // by react-flow__viewport-portal before the CSS fix, and serves as a reliable fallback.
  const handleContainerClick = useCallback((event) => {
    if (!onEdgeClick) return;
    const key = hitTestEdges(event.clientX, event.clientY);
    if (key) {
      event.stopPropagation();
      onEdgeClick(key, event.clientX, event.clientY);
    }
  }, [hitTestEdges, onEdgeClick]);

  return (
    // nodrag tells react-grid-layout not to initiate tile dragging when mousedown
    // starts inside this container, which lets ReactFlow handle panning and clicks.
    <div
      ref={containerRef}
      className="nodrag"
      style={{ position: "absolute", inset: 0, cursor: cursorPointer ? "pointer" : "default" }}
      onMouseMove={handleMouseMove}
      onClick={handleContainerClick}
    >
      <EdgeClickContext.Provider value={onEdgeClick ?? null}>
      <ReactFlow
        onInit={(instance) => { rfRef.current = instance; }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        nodesDraggable={false}
        nodesConnectable={false}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        panOnDrag
        proOptions={{ hideAttribution: true }}
        minZoom={0.1}
        onPaneClick={handlePaneClick}
      >
        <Controls showInteractive={false} />
        <Background variant="dots" gap={16} size={1} color="#e2e8f0" />
      </ReactFlow>
      </EdgeClickContext.Provider>
    </div>
  );
};

export default SplitConformanceGraph;
